import { Box } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

import { DeadlineLevel, daysLeft, deadlineLevel } from './inquiry-deadline';

const c = tokens.color;

const INK: Record<DeadlineLevel, string> = {
    today: c.crit,
    soon: c.warnInk,
    normal: c.ink,
};

const TONE: Record<Exclude<DeadlineLevel, 'normal'>, object> = {
    today: { color: c.crit, backgroundColor: c.critSoft, borderColor: c.critLine },
    soon: { color: c.warnInk, backgroundColor: c.warnSoft, borderColor: c.warnLine },
};

/** The "!" an urgent deadline carries, so it never rests on colour alone. */
const Mark = ({ level }: { level: Exclude<DeadlineLevel, 'normal'> }): ReactElement => (
    <Box
        component="span"
        aria-hidden
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: '1px solid',
            fontSize: '11px',
            fontWeight: 700,
            flexShrink: 0,
            ...TONE[level],
        }}
    >
        !
    </Box>
);

/**
 * The deadline the way a supplier reads it: how long is left, then the date
 * (R-257). Only an inquiry still waiting for an offer counts down; for the
 * others the date is all there is to say.
 */
export const PaloInquiryDeadline = ({ endDate, open }: { endDate: Date; open: boolean }): ReactElement => {
    const intl = useIntl();

    if (!open) {
        return <>{intl.formatDate(endDate, { day: '2-digit', month: '2-digit', year: 'numeric' })}</>;
    }

    const date = intl.formatDate(endDate, { day: '2-digit', month: '2-digit' });
    const days = daysLeft(endDate);
    const level = deadlineLevel(endDate);
    const messageId =
        days < 0
            ? 'app.inquiries.deadline.over'
            : 0 === days
            ? 'app.inquiries.deadline.today'
            : 'app.inquiries.deadline.inDays';

    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ color: INK[level], fontWeight: 600 }}>
                <FormattedMessage id={messageId} values={{ days, date }} />
            </Box>
            {'normal' !== level && <Mark level={level} />}
        </Box>
    );
};

/** One of the urgency pills in the head of the open list. */
export const PaloDeadlinePill = ({
    level,
    children,
}: {
    level: Exclude<DeadlineLevel, 'normal'>;
    children: ReactNode;
}): ReactElement => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '999px',
            border: '1px solid',
            padding: '2px 10px',
            fontSize: '12.5px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            ...TONE[level],
        }}
    >
        !&nbsp;{children}
    </Box>
);
